import { useState, FormEvent, useRef } from 'react';
import { PortionStepper } from '../ui/PortionStepper';
import { TimePicker } from '../ui/TimePicker';
import { ImageUploader } from '../ui/ImageUploader';
import { convertToAVIF, getPreferredExtension } from '../../lib/imageUtils';
import { apiUrl } from '../../lib/api';

interface MealFormProps {
	selectedDate: string;
	onSuccess?: (message: string) => void;
}

type MealSubTab = 'breakfast' | 'snack1' | 'lunch' | 'snack2' | 'dinner';

const mealSubTabs: { id: MealSubTab; label: string }[] = [
	{ id: 'breakfast', label: 'Sarapan' },
	{ id: 'snack1', label: 'Snack 1' },
	{ id: 'lunch', label: 'Makan Siang' },
	{ id: 'snack2', label: 'Snack 2' },
	{ id: 'dinner', label: 'Makan Malam' },
];

/**
 * MealForm Component
 *
 * Form for logging meal entries with portion steppers.
 * Includes sub-tabs for different meal types.
 * Posts to /api/meals endpoint.
 */
export function MealForm({ selectedDate, onSuccess }: MealFormProps) {
	const [activeSubTab, setActiveSubTab] = useState<MealSubTab>('breakfast');
	const [description, setDescription] = useState('');
	const [carbPortions, setCarbPortions] = useState(1);
	const [proteinPortions, setProteinPortions] = useState(1);
	const [startTime, setStartTime] = useState('');
	const [endTime, setEndTime] = useState('');
	const [notes, setNotes] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [pendingBlobs, setPendingBlobs] = useState<Blob[]>([]);
	const [uploaderKey, setUploaderKey] = useState(0); // Key to reset ImageUploader

	const resetForm = () => {
		setDescription('');
		setCarbPortions(1);
		setProteinPortions(1);
		setStartTime('');
		setEndTime('');
		setNotes('');
		setPendingBlobs([]);
		setUploaderKey((k) => k + 1); // Reset ImageUploader
	};

	const uploadImages = async (entryId: string, blobs: Blob[]) => {
		const ext = await getPreferredExtension();
		for (const blob of blobs) {
			// Convert to optimal format (AVIF or WebP)
			const result = await convertToAVIF(blob);

			const formData = new FormData();
			formData.append('file', result.blob, `image-${Date.now()}.${ext}`);
			formData.append('entryType', 'meal');
			formData.append('entryId', entryId);
			formData.append('width', result.width.toString());
			formData.append('height', result.height.toString());

			const res = await fetch(apiUrl('/api/images'), {
				method: 'POST',
				credentials: 'include',
				body: formData,
			});
			if (!res.ok) {
				throw new Error('Gagal mengunggah foto. Silakan coba lagi.');
			}
		}
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);
		setError(null);

		// Validate description
		if (!description.trim()) {
			setError('Deskripsi makanan harus diisi');
			setIsSubmitting(false);
			return;
		}

		// Validate time order if both provided
		if (startTime && endTime && startTime >= endTime) {
			setError('Waktu selesai harus setelah waktu mulai');
			setIsSubmitting(false);
			return;
		}

		try {
			// Create entry
			const res = await fetch(apiUrl('/api/meals'), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					date: selectedDate,
					mealType: activeSubTab,
					description: description.trim(),
					carbPortions: carbPortions.toString(),
					proteinPortions: proteinPortions.toString(),
					startTime: startTime ? `${selectedDate}T${startTime}:00` : undefined,
					endTime: endTime ? `${selectedDate}T${endTime}:00` : undefined,
					notes: notes || undefined,
				}),
			});

			if (!res.ok) {
				const data = await res.json();
				throw new Error(data.error || 'Gagal menyimpan data');
			}

			const data = await res.json();

			// Upload images if any
			if (pendingBlobs.length > 0) {
				await uploadImages(data.id, pendingBlobs);
			}

			// Success
			const tabLabel = mealSubTabs.find((t) => t.id === activeSubTab)?.label;
			onSuccess?.(`${tabLabel} berhasil dicatat!`);
			resetForm();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Terjadi kesalahan');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-5">
			<h2 className="text-lg font-semibold text-gray-900">Catat Makanan</h2>

			{/* Sub-tabs for meal type */}
			<div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
				{mealSubTabs.map((tab) => (
					<button
						key={tab.id}
						type="button"
						onClick={() => setActiveSubTab(tab.id)}
						className={`px-4 py-2.5 min-h-11 text-sm font-medium rounded-full transition ${
							activeSubTab === tab.id
								? 'bg-blue-100 text-blue-700'
								: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
						}`}
					>
						{tab.label}
					</button>
				))}
			</div>

			{/* Error Display */}
			{error && (
				<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
					{error}
				</div>
			)}

			{/* Description Input */}
			<div>
				<label
					htmlFor="meal-description"
					className="block text-sm font-medium text-gray-700 mb-2"
				>
					Deskripsi Makanan <span className="text-red-500">*</span>
				</label>
				<textarea
					id="meal-description"
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					rows={3}
					placeholder="Contoh: Nasi goreng dengan telur dan sayur"
					required
					disabled={isSubmitting}
					className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
				/>
			</div>

			{/* Portion Steppers */}
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
				<PortionStepper
					value={carbPortions}
					onChange={setCarbPortions}
					label="Karbohidrat"
					guideText="1 porsi = 1 centong nasi"
					disabled={isSubmitting}
				/>
				<PortionStepper
					value={proteinPortions}
					onChange={setProteinPortions}
					label="Protein"
					guideText="1 porsi = 1 potong ayam/ikan"
					disabled={isSubmitting}
				/>
			</div>

			{/* Time Pickers */}
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
				<TimePicker
					value={startTime}
					onChange={setStartTime}
					label="Waktu Mulai (opsional)"
					id="meal-start-time"
					disabled={isSubmitting}
				/>
				<TimePicker
					value={endTime}
					onChange={setEndTime}
					label="Waktu Selesai (opsional)"
					id="meal-end-time"
					disabled={isSubmitting}
				/>
			</div>

			{/* Notes Input */}
			<div>
				<label
					htmlFor="meal-notes"
					className="block text-sm font-medium text-gray-700 mb-2"
				>
					Catatan (opsional)
				</label>
				<textarea
					id="meal-notes"
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
					rows={2}
					placeholder="Catatan tambahan..."
					disabled={isSubmitting}
					className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
				/>
			</div>

			{/* Image Upload Section (inline) */}
			<div>
				<label className="block text-sm font-medium text-gray-700 mb-2">
					Foto (opsional)
				</label>
				<ImageUploader
					key={uploaderKey}
					entryType="meal"
					onPendingBlobsChange={setPendingBlobs}
					disabled={isSubmitting}
				/>
			</div>

			{/* Submit Button - Sticky on mobile for easy access */}
			<div className="sticky bottom-0 pt-4 -mx-6 px-6 pb-1 bg-white sm:static sm:mx-0 sm:px-0 sm:pb-0 sm:bg-transparent">
				<button
					type="submit"
					disabled={isSubmitting}
					className="w-full bg-blue-600 text-white py-3 px-4 min-h-12 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium shadow-lg sm:shadow-none"
				>
					{isSubmitting ? 'Menyimpan...' : 'Simpan'}
				</button>
			</div>
		</form>
	);
}
